<script lang="ts">
  import { t } from '$core/i18n';
  import {
    socialStore,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    removeFriend
  } from '$features/social';
  import AddFriendModal from '$ui/components/AddFriendModal.svelte';

  let {
    onOpenGroups
  }: {
    onOpenGroups: () => void;
  } = $props();

  type Tab = 'incoming' | 'outgoing' | 'friends';
  let tab = $state<Tab>('incoming');
  let showAdd = $state(false);
  let busy = $state(false);

  $effect(() => {
    void socialStore.refreshAll(true);

    // Polling cada 15s mientras el panel está abierto, para reflejar
    // cambios hechos desde otras cuentas (aceptar/rechazar/eliminar).
    const intervalId = window.setInterval(() => {
      void socialStore.refreshAll(true);
    }, 15 * 1000);
    return () => window.clearInterval(intervalId);
  });

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && !showAdd) {
      e.preventDefault();
    }
  }

  async function onAccept(id: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await acceptFriendRequest(id);
      await socialStore.refreshAll(true);
    } finally {
      busy = false;
    }
  }

  async function onDecline(id: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await declineFriendRequest(id);
      await socialStore.refreshAll(true);
    } finally {
      busy = false;
    }
  }

  async function onCancel(id: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await cancelFriendRequest(id);
      await socialStore.refreshAll(true);
    } finally {
      busy = false;
    }
  }

  async function onRemove(friendId: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await removeFriend(friendId);
      await socialStore.refreshAll(true);
    } finally {
      busy = false;
    }
  }

  function onSentRequest(): void {
    void socialStore.refreshAll(true);
  }

  function initialOf(name: string | null | undefined): string {
    if (!name) return '?';
    const trimmed = name.trim();
    return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : '?';
  }

  function shortCountry(c: string | null | undefined): string {
    if (!c) return '';
    return c === 'pirate' ? t('leaderboard.countryPirate') : c.toUpperCase();
  }
</script>

<svelte:window on:keydown={onKeydown} />

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('friends.title')}</h1>
    <div class="controls-right">
      <button class="secondary" onclick={onOpenGroups}>
        {t('groups.title')}
      </button>
      <button class="primary" onclick={() => (showAdd = true)}>
        + {t('friends.add')}
      </button>
    </div>
  </header>

  <div class="controls">
    <div class="tabs">
      <button
        class="tab"
        class:active={tab === 'incoming'}
        onclick={() => (tab = 'incoming')}
      >
        {t('friends.tabIncoming')}
        {#if socialStore.incoming.length > 0}
          <span class="count">{socialStore.incoming.length}</span>
        {/if}
      </button>
      <button
        class="tab"
        class:active={tab === 'outgoing'}
        onclick={() => (tab = 'outgoing')}
      >
        {t('friends.tabOutgoing')}
        {#if socialStore.outgoing.length > 0}
          <span class="count">{socialStore.outgoing.length}</span>
        {/if}
      </button>
      <button
        class="tab"
        class:active={tab === 'friends'}
        onclick={() => (tab = 'friends')}
      >
        {t('friends.tabFriends')}
        {#if socialStore.friends.length > 0}
          <span class="count">{socialStore.friends.length}</span>
        {/if}
      </button>
    </div>
  </div>

    {#if socialStore.loading && socialStore.friends.length === 0 && socialStore.incoming.length === 0}
      <p class="empty">{t('friends.loading')}</p>
    {:else if tab === 'incoming'}
      {#if socialStore.incoming.length === 0}
        <p class="empty">{t('friends.noIncoming')}</p>
      {:else}
        <ul class="list">
          {#each socialStore.incoming as req (req.id)}
            <li class="row">
              <div class="row-info">
                <span class="avatar-wrap">
                  <span class="avatar-initial">{initialOf(req.otherUser?.displayName)}</span>
                  {#if req.otherUser?.avatarUrl}
                    <img src={req.otherUser.avatarUrl} alt="" loading="lazy" />
                  {/if}
                </span>
                <div class="row-texts">
                  <span class="row-name">{req.otherUser?.displayName ?? t('friends.unknown')}</span>
                  <span class="row-sub">{shortCountry(req.otherUser?.country)}</span>
                </div>
              </div>
              <div class="row-actions">
                <button
                  class="primary"
                  disabled={busy}
                  onclick={() => void onAccept(req.id)}
                >
                  {t('friends.accept')}
                </button>
                <button
                  class="secondary"
                  disabled={busy}
                  onclick={() => void onDecline(req.id)}
                >
                  {t('friends.decline')}
                </button>
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    {:else if tab === 'outgoing'}
      {#if socialStore.outgoing.length === 0}
        <p class="empty">{t('friends.noOutgoing')}</p>
      {:else}
        <ul class="list">
          {#each socialStore.outgoing as req (req.id)}
            <li class="row">
              <div class="row-info">
                <span class="avatar-wrap">
                  <span class="avatar-initial">{initialOf(req.otherUser?.displayName)}</span>
                  {#if req.otherUser?.avatarUrl}
                    <img src={req.otherUser.avatarUrl} alt="" loading="lazy" />
                  {/if}
                </span>
                <div class="row-texts">
                  <span class="row-name">{req.otherUser?.displayName ?? t('friends.unknown')}</span>
                  <span class="row-sub">{shortCountry(req.otherUser?.country)}</span>
                </div>
              </div>
              <div class="row-actions">
                <button
                  class="secondary"
                  disabled={busy}
                  onclick={() => void onCancel(req.id)}
                >
                  {t('friends.cancel')}
                </button>
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    {:else if tab === 'friends'}
      {#if socialStore.friends.length === 0}
        <p class="empty">{t('friends.noFriends')}</p>
      {:else}
        <ul class="list">
          {#each socialStore.friends as friend (friend.userId)}
            <li class="row">
              <div class="row-info">
                <span class="avatar-wrap">
                  <span class="avatar-initial">{initialOf(friend.profile?.displayName)}</span>
                  {#if friend.profile?.avatarUrl}
                    <img src={friend.profile.avatarUrl} alt="" loading="lazy" />
                  {/if}
                </span>
                <div class="row-texts">
                  <span class="row-name">{friend.profile?.displayName ?? t('friends.unknown')}</span>
                  <span class="row-sub">{shortCountry(friend.profile?.country)}</span>
                </div>
              </div>
              <div class="row-actions">
                <button
                  class="danger"
                  disabled={busy}
                  onclick={() => void onRemove(friend.userId)}
                >
                  {t('friends.remove')}
                </button>
              </div>
            </li>
          {/each}
        </ul>
      {/if}
  {/if}

  {#if showAdd}
    <AddFriendModal
      onClose={() => (showAdd = false)}
      onSent={onSentRequest}
    />
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    max-width: 900px;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .controls-right {
    display: flex;
    gap: var(--space-2);
  }

  .controls {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .tabs {
    display: flex;
    gap: var(--space-2);
  }

  .tab {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    height: 32px;
    padding: 0 var(--space-3);
    font-size: var(--text-sm);
    font-family: inherit;
    cursor: pointer;
    color: var(--color-text-secondary);
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    transition:
      background var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);
  }

  .tab:hover {
    color: var(--color-text-primary);
    border-color: var(--color-border-strong);
  }

  .tab.active {
    color: var(--color-accent-fg);
    background: var(--color-accent);
    border-color: var(--color-accent);
  }

  .tab:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    background: color-mix(in oklch, var(--color-accent-fg) 22%, transparent);
    border-radius: var(--radius-full);
    line-height: 1;
  }

  .tab:not(.active) .count {
    background: var(--color-surface-hover);
    color: var(--color-text-muted);
  }

  .empty {
    padding: var(--space-8) var(--space-4);
    margin: 0;
    text-align: center;
    color: var(--color-text-muted);
    font-size: var(--text-body);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    min-height: 56px;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .row-info {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
    flex: 1;
  }

  .avatar-wrap {
    position: relative;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    flex: 0 0 auto;
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .avatar-initial {
    font-size: 13px;
    font-family: var(--font-mono);
    color: var(--color-text-muted);
    text-transform: uppercase;
    line-height: 1;
  }

  .avatar-wrap img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .row-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .row-name {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .row-sub {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .row-actions {
    display: flex;
    gap: var(--space-2);
    flex: 0 0 auto;
  }

  .primary {
    height: 32px;
    padding: 0 var(--space-3);
    background: var(--color-accent);
    color: var(--color-accent-fg);
    border: var(--border-width) solid var(--color-accent);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out);
  }

  .primary:hover:not(:disabled) {
    background: var(--color-accent-hover);
    border-color: var(--color-accent-hover);
  }

  .primary:focus-visible,
  .secondary:focus-visible,
  .danger:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .primary:disabled,
  .secondary:disabled,
  .danger:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .secondary {
    height: 32px;
    padding: 0 var(--space-3);
    background: transparent;
    color: var(--color-text-secondary);
    border: var(--border-width) solid var(--color-border-strong);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .secondary:hover:not(:disabled) {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
  }

  .danger {
    height: 32px;
    padding: 0 var(--space-3);
    background: transparent;
    color: var(--color-danger);
    border: var(--border-width) solid var(--color-danger);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .danger:hover:not(:disabled) {
    color: var(--color-danger-fg);
    background: var(--color-danger);
  }
</style>